import { useEffect, useRef, useCallback } from 'react';
import { CA_COUNTIES_CONFIG } from '../../../mocks/caForeclosures';
import { NV_COUNTIES_CONFIG } from '../../../mocks/nvForeclosures';
import { AZ_COUNTIES_CONFIG } from '../../../mocks/azForeclosures';
import { CombinedProperty } from '../types';

declare global {
  interface Window {
    L: any;
  }
}

interface CombinedLeafletMapProps {
  properties: CombinedProperty[];
  focusState: string | null;
  onFocusHandled: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  Active: '#22c55e',
  Pending: '#f59e0b',
  Upcoming: '#06b6d4',
  Sold: '#9ca3af',
};

const STATE_CENTERS: Record<string, [number, number]> = {
  CA: [36.7783, -119.4179],
  AZ: [34.0489, -111.0937],
  NV: [39.5, -116.5],
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const el = document.createElement('script');
    el.src = src;
    el.onload = () => resolve();
    el.onerror = reject;
    document.head.appendChild(el);
  });

const loadStyle = (href: string): void => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const el = document.createElement('link');
  el.rel = 'stylesheet';
  el.href = href;
  document.head.appendChild(el);
};

const getAllCountiesConfig = () => ({
  ...Object.fromEntries(Object.entries(CA_COUNTIES_CONFIG).map(([k, v]) => [`CA:${k}`, v])),
  ...Object.fromEntries(Object.entries(NV_COUNTIES_CONFIG).map(([k, v]) => [`NV:${k}`, v])),
  ...Object.fromEntries(Object.entries(AZ_COUNTIES_CONFIG).map(([k, v]) => [`AZ:${k}`, v])),
});

export const CombinedLeafletMap: React.FC<CombinedLeafletMapProps> = ({ properties, focusState, onFocusHandled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const clusterGroupRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  const initializedRef = useRef(false);

  const STATE_BADGE_COLORS: Record<string, string> = {
    CA: '#f97316',
    NV: '#f59e0b',
    AZ: '#22c55e',
  };

  const buildPopup = (p: CombinedProperty) => `
    <div style="min-width:260px;font-family:'Inter',sans-serif;background:#1f2937;color:#f9fafb;border-radius:10px;overflow:hidden;">
      <div style="background:#111827;padding:10px 14px;border-bottom:1px solid #374151;">
        <div style="font-weight:700;font-size:13px;color:#f9fafb;margin-bottom:4px;">${p.address}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <span style="background:${STATE_BADGE_COLORS[p.state]}22;color:${STATE_BADGE_COLORS[p.state]};border:1px solid ${STATE_BADGE_COLORS[p.state]}44;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;">${p.state}</span>
          <span style="background:${p.countyColor}22;color:${p.countyColor};border:1px solid ${p.countyColor}44;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:600;">${p.county} County</span>
          <span style="background:${STATUS_COLORS[p.status]}22;color:${STATUS_COLORS[p.status]};border:1px solid ${STATUS_COLORS[p.status]}44;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:600;">${p.status}</span>
        </div>
      </div>
      <div style="padding:12px 14px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 12px;">
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Parcel #</div><div style="color:#e5e7eb;font-size:11px;font-weight:600;margin-top:1px;">${p.parcelNumber}</div></div>
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Type</div><div style="color:#e5e7eb;font-size:11px;font-weight:600;margin-top:1px;">${p.propertyType}</div></div>
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Taxes Owed</div><div style="color:#ef4444;font-size:13px;font-weight:700;margin-top:1px;">${fmt(p.taxesOwed)}</div></div>
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Assessed Value</div><div style="color:#e5e7eb;font-size:13px;font-weight:700;margin-top:1px;">${fmt(p.assessedValue)}</div></div>
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Min Bid</div><div style="color:#22c55e;font-size:13px;font-weight:700;margin-top:1px;">${fmt(p.minBid)}</div></div>
          <div><div style="color:#9ca3af;font-size:10px;text-transform:uppercase;letter-spacing:.05em;">Auction Date</div><div style="color:#f59e0b;font-size:11px;font-weight:600;margin-top:1px;">${new Date(p.auctionDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div></div>
        </div>
      </div>
    </div>
  `;

  const createMarkerIcon = useCallback((p: CombinedProperty) => {
    const L = window.L;
    const color = p.countyColor;
    const statusColor = STATUS_COLORS[p.status] || '#9ca3af';
    const stateLabel = p.state;
    return L.divIcon({
      className: '',
      html: `<div style="position:relative;width:30px;height:30px;">
        <div style="width:30px;height:30px;border-radius:50%;background:${color};border:3px solid #111827;box-shadow:0 0 8px ${color}88;display:flex;align-items:center;justify-content:center;flex-direction:column;">
          <div style="font-size:7px;font-weight:800;color:#111827;line-height:1;">${stateLabel}</div>
          <div style="width:7px;height:7px;border-radius:50%;background:${statusColor};margin-top:1px;"></div>
        </div>
      </div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -18],
    });
  }, []);

  const initMap = useCallback(async () => {
    if (initializedRef.current || !containerRef.current) return;
    loadStyle('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    loadStyle('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css');
    loadStyle('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css');
    await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
    await loadScript('https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js');

    const L = window.L;
    if (!L || mapRef.current) return;
    initializedRef.current = true;

    const map = L.map(containerRef.current, {
      center: [37.5, -114.5],
      zoom: 5,
      zoomControl: false,
      preferCanvas: true,
    });

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="width:42px;height:42px;border-radius:50%;background:rgba(245,158,11,0.9);border:2px solid #f59e0b;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;color:#111827;box-shadow:0 0 14px rgba(245,158,11,0.6);">${count}</div>`,
          className: '',
          iconSize: [42, 42],
          iconAnchor: [21, 21],
        });
      },
    });

    map.addLayer(clusterGroup);
    mapRef.current = map;
    clusterGroupRef.current = clusterGroup;
  }, []);

  useEffect(() => {
    initMap();
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        clusterGroupRef.current = null;
        markersRef.current.clear();
        initializedRef.current = false;
      }
    };
  }, [initMap]);

  useEffect(() => {
    const tryUpdate = () => {
      const L = window.L;
      if (!L || !mapRef.current || !clusterGroupRef.current) {
        setTimeout(tryUpdate, 300);
        return;
      }
      const cluster = clusterGroupRef.current;
      cluster.clearLayers();
      markersRef.current.clear();

      properties.forEach(p => {
        const marker = L.marker([p.lat, p.lng], { icon: createMarkerIcon(p) });
        marker.bindPopup(buildPopup(p), { className: 'dark-popup-combined', maxWidth: 300 });
        cluster.addLayer(marker);
        markersRef.current.set(p.id, marker);
      });
    };

    if (initializedRef.current) {
      tryUpdate();
    } else {
      const t = setTimeout(tryUpdate, 800);
      return () => clearTimeout(t);
    }
  }, [properties, createMarkerIcon]);

  useEffect(() => {
    if (!focusState || !mapRef.current) return;
    const center = STATE_CENTERS[focusState];
    if (center) {
      const zoom = focusState === 'All' ? 5 : 7;
      mapRef.current.flyTo(center, zoom, { duration: 1.2 });
    }
    onFocusHandled();
  }, [focusState, onFocusHandled]);

  return (
    <>
      <style>{`
        .dark-popup-combined .leaflet-popup-content-wrapper {
          background: transparent !important;
          border: none !important;
          box-shadow: 0 20px 60px rgba(0,0,0,0.8) !important;
          padding: 0 !important;
          border-radius: 10px !important;
          overflow: hidden !important;
        }
        .dark-popup-combined .leaflet-popup-tip-container { display: none !important; }
        .dark-popup-combined .leaflet-popup-content { margin: 0 !important; }
        .leaflet-container { background: #0f172a !important; }
        .leaflet-control-zoom a {
          background: #1f2937 !important;
          color: #e5e7eb !important;
          border-color: #374151 !important;
        }
        .leaflet-control-zoom a:hover { background: #374151 !important; }
        .leaflet-control-attribution {
          background: rgba(17,24,39,0.8) !important;
          color: #6b7280 !important;
          font-size: 10px !important;
        }
        .leaflet-control-attribution a { color: #9ca3af !important; }
      `}</style>
      <div ref={containerRef} className="w-full h-full" />
    </>
  );
};

export default CombinedLeafletMap;
